// @ts-strict-ignore
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { addTrackingReducerConnection, getTrackingDispatchers } from '@owc/connection-aem-tracking';
// TODO: Fix with MBCC-2667 - request proper type exports from OWC (e.g. index.js with index.d.ts instead of a types.d.ts)
import { OwcTrackingDispatchers } from '@owc/connection-aem-tracking/dist/connections/aem-tracking/types';
import { combineLatest, from, of, switchMap } from 'rxjs';
import { first, mergeMap, startWith } from 'rxjs/operators';

import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { selectIsInIntegratedMode } from 'src/app/store/common/common.selectors';
import { selectRouteState } from 'src/app/store/router/router.selectors';
import { selectRuntimeVariablesOrUndefined } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { filterNotUndefined, filterTruthy } from 'src/app/utils/functions/rx-filters';
import { WINDOW } from 'src/app/utils/window.token';

import { TrackingAction } from './models/tracking-action.enum';
import { TrackingCategory } from './models/tracking-category.enum';
import { TrackingEventType } from './models/tracking-event-type.enum';
import { TrackingLabel } from './models/tracking-label.enum';
import { TrackingEventDetail, TrackingInput, TrackingObject } from './models/tracking-object.interface';
import { TrackingPagename } from './models/tracking-pagename.enum';
import { TrackingDataService } from './tracking-data.service';

const COUNTRY_CODE_CHINA = 'CN';

export const createTrackingInput = (
  event: TrackingEventType,
  label: string | TrackingLabel,
  category: TrackingCategory,
  action: TrackingAction
): TrackingInput => ({
  event,
  clickData: { label, category, action }
});

export const createClickEventTrackingInput = (
  label: string,
  category: TrackingCategory,
  action: TrackingAction
): TrackingInput => createTrackingInput(TrackingEventType.Click, label, category, action);

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  private seamlessTrackingConnection: OwcTrackingDispatchers;
  private readonly seamlessTrackingEventQueue: TrackingObject[] = [];

  public constructor(
    private readonly trackingDataService: TrackingDataService,
    private readonly store: Store,
    @Inject(WINDOW) private readonly window: Window
  ) {
    this.store
      .select(selectIsInIntegratedMode)
      .pipe(
        filterTruthy(),
        first(),
        switchMap(() => from(addTrackingReducerConnection())),
        switchMap(() => from(getTrackingDispatchers()))
      )
      .subscribe(connection => {
        this.seamlessTrackingConnection = connection;
        this.resolveQueue();
      });
  }

  // sets callback function for the usabilla feedback widget
  // It's needed to pass the clicks to the trackingservice
  public initiateInWidgetTracking(): void {
    // https://help.surveymonkey.com/en/getfeedback/integrations/event-callbacks/
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.window.usabilla(
      'setEventCallback',
      // eslint-disable-next-line complexity
      (
        category: string,
        action: string,
        label: string | null,
        value: string | null,
        userData: Record<string, Record<string, string>> | null
      ) => {
        if (action) {
          let cat = userData?.data?.pages[userData?.data?.pages.length - 1];
          cat = cat === 'End' ? userData?.data?.pages[userData?.data?.pages.length - 2] : cat;
          this.sendTrackingData({
            event: TrackingEventType.Click,
            clickData: {
              category: TrackingCategory.Feature,
              action: TrackingAction.ConfiguratorFeedback,
              label: cat === 'Open_feedback' ? cat.toLowerCase() : `${cat.toLowerCase()}:${userData?.data?.data[cat]}`
            }
          });
        }
      }
    );
  }

  public trackClick(clickData: TrackingEventDetail): void {
    if (clickData.label) {
      this.store
        .select(selectRouteState)
        .pipe(filterNotUndefined(), first())
        .subscribe(routeState => {
          let state: AppRoutePath | TrackingPagename | string = TrackingPagename.Motorization;

          if (routeState.isConfiguration) {
            state = routeState.isSummary ? TrackingPagename.Summary : routeState.configurationStep;
          }

          const trackingInput: TrackingInput = {
            event: TrackingEventType.Click,
            clickData,
            routeState: state
          };
          this.sendTrackingData(trackingInput);
        });
    }
  }

  public sendTrackingData(trackingInput: TrackingInput): void {
    this.trackingDataService
      .getTrackingData(trackingInput)
      .pipe(
        first(),
        mergeMap((data: TrackingObject) =>
          combineLatest([
            this.store.pipe(selectRuntimeVariablesOrUndefined).pipe(startWith(null)),
            of(data),
            this.store.select(selectIsInIntegratedMode)
          ])
        )
      )
      .subscribe(([params, data, isInIntegratedMode]) => {
        if (!data) {
          return;
        }
        if (isInIntegratedMode && params?.country !== COUNTRY_CODE_CHINA) {
          this.queueSeamlessTrackingEvent(data);
        } else {
          this.window.dataLayer_ow = this.window.dataLayer_ow || [];
          this.window.dataLayer_ow.push(data);
        }
      });
  }

  /**
   * collects all TrackingObjects in case the seamless
   * tracking connection was not yet established.
   * if it is, resolve the queue
   *
   * @param data - which should be send to OneWeb dataLayer
   */
  private queueSeamlessTrackingEvent(data: TrackingObject): void {
    this.seamlessTrackingEventQueue.push(data);

    if (!this.seamlessTrackingConnection) {
      return;
    }

    this.resolveQueue();
  }

  /**
   * if the connection was established and Events are in the Queue, send them to the seamless data-layer
   */
  private resolveQueue(): void {
    while (this.seamlessTrackingConnection && this.seamlessTrackingEventQueue.length > 0) {
      this.seamlessTrackingConnection.pushTrackingEvent(this.seamlessTrackingEventQueue.shift());
    }
  }
}
